/**
 * Weekly Report Card
 *
 * @layer widgets
 * @slice weekly-report-card
 * @segment ui
 */

import type { ReactNode } from 'react';
import { AnalyticsPercentTrend } from '@features/analytics-weekly';
import { cn } from '@shared/lib/utils';
import type {
  WeeklyReportCardKpi,
  WeeklyReportCardMetric,
  WeeklyReportCardSection,
} from '@shared/types';
import {
  Card,
  CardContent,
  CardHeader,
  DashboardCardRow,
  Heading,
  KpiCard,
  Stack,
  Text,
} from '@shared/ui';

type WeeklyReportCardProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  kpis?: WeeklyReportCardKpi[];
  sections: WeeklyReportCardSection[];
  insights?: ReactNode;
  insightsLabel?: string;
  className?: string;
  locale?: string;
  formatters?: {
    signedPercent?: (value: number) => string;
    percent?: (value: number) => string;
    value?: (value: number) => string;
  };
};

const defaultSignedPercent = (value: number) => {
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formatted = formatter.format(Math.abs(value));
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatted}%`;
};

const defaultPercent = (value: number) => {
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatter.format(value)}%`;
};

export function WeeklyReportCard({
  title,
  subtitle,
  kpis,
  sections,
  insights,
  insightsLabel,
  className,
  locale,
  formatters,
}: WeeklyReportCardProps) {
  const signedPercent = formatters?.signedPercent ?? defaultSignedPercent;
  const percent = formatters?.percent ?? defaultPercent;

  return (
    <Card className={cn('shadow-lg transition-colors hover:border-primary', className)}>
      <CardHeader className="space-y-1">
        <Heading as="h3" size="xl" className="text-lg sm:text-xl whitespace-normal">
          {title}
        </Heading>
        {subtitle ? (
          <Text size="xs" color="muted">
            {subtitle}
          </Text>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {kpis && kpis.length > 0 ? (
          <DashboardCardRow columns={3}>
            {kpis.map((kpi, index) => (
              <KpiCard
                key={`${kpi.label}-${String(index)}`}
                title={kpi.label}
                value={kpi.value}
                subtext={kpi.subtext}
                trend={
                  kpi.percentChange === null || kpi.percentChange === undefined ? null : (
                    <AnalyticsPercentTrend
                      context={kpi.comparisonLabel}
                      locale={locale}
                      positiveIsGood={kpi.positiveIsGood}
                      value={kpi.percentChange}
                    />
                  )
                }
                locale={locale}
                className="h-full"
              />
            ))}
          </DashboardCardRow>
        ) : null}

        <Stack gap="md">
          {sections.map((section, index) => (
            <Stack key={`${section.title}-${String(index)}`} gap="sm">
              <Text size="xs" className="font-semibold text-card-foreground">
                {section.title}
              </Text>
              <Stack gap="xs">
                {section.items?.map((item, itemIndex) => (
                  <MetricLine
                    key={`${item.label}-${String(itemIndex)}`}
                    item={item}
                    formatters={{ signedPercent, percent }}
                  />
                ))}
              </Stack>
              {section.subsections?.map((subsection, subIndex) => (
                <Stack key={`${subsection.title ?? 'sub'}-${String(subIndex)}`} gap="xs">
                  {subsection.title ? (
                    <Text size="xs" className="font-semibold text-muted-foreground">
                      {subsection.title}
                    </Text>
                  ) : null}
                  <Stack gap="xs">
                    {subsection.items.map((item, itemIndex) => (
                      <MetricLine
                        key={`${item.label}-${String(itemIndex)}`}
                        item={item}
                        formatters={{ signedPercent, percent }}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>

        {insights ? (
          <Stack gap="xs">
            <Text size="xs" className="font-semibold text-card-foreground">
              {insightsLabel ?? 'Insights'}
            </Text>
            <Text size="sm" color="muted">
              {insights}
            </Text>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MetricLine({
  item,
  formatters,
}: {
  item: WeeklyReportCardMetric;
  formatters: { signedPercent: (value: number) => string; percent: (value: number) => string };
}) {
  const suffix = item.suffix;
  const formatValue = suffix?.tone === 'change' ? formatters.signedPercent : formatters.percent;
  const shouldRenderSuffix = Boolean(suffix && suffix.value !== null);
  const formattedSuffix = suffix && suffix.value !== null ? formatValue(suffix.value) : null;

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
      <Text size="xs" color="muted" className="text-left">
        {item.label}
      </Text>
      <Text
        size="xs"
        className="break-words text-left font-medium text-card-foreground sm:text-right"
      >
        {item.value}
        {shouldRenderSuffix && suffix && formattedSuffix ? (
          suffix.tone === 'change' ? (
            <AnalyticsPercentTrend
              appearance="inline"
              className="ml-1"
              context={suffix.label}
              formatValue={formatValue}
              positiveIsGood={suffix.positiveIsGood}
              value={suffix.value}
              wrap="wrap"
            />
          ) : (
            <span className="ml-1 text-muted-foreground">
              ({formattedSuffix} {suffix.label})
            </span>
          )
        ) : null}
      </Text>
    </div>
  );
}
